/*  MUI  */
import {
    Add,
    AssignmentTurnedIn,
    ExpandMore,
    Remove
} from '@mui/icons-material';
import {
    Box,
    Button,
    Checkbox,
    MenuItem,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField
} from '@mui/material';

/*  Shared  */
import { DarkTooltip, TooltipQ } from 'application/Shared/Components/Tooltips';
import { Helpers } from 'application/Shared/Helpers';

/*  React  */
import React, { Component } from 'react';
import { useTranslation, withTranslation } from 'react-i18next';

/*  NPM  */
const { v4: uuidv4 } = require('uuid');

/*  Constants  */
const { isset, isEmpty } = Helpers;
const defaultFields = [
    { field: 'id' },
    { field: 'new' },
    { field: 'disabled' },
    { field: 'checkAction' },
    { field: 'actionDisabled' },
    { field: 'all' },
    { field: 'community' }
];

/*  Initial State  */
const state = () => ({
    added: {},
    colSpan: 1,
    endFields: [],
    loaded: false,
    modified: {},
    removed: {},
    rows: []
});

const RenderHeader = ({
    actionsTooltip,
    endFields,
    fields,
    noActions,
    showIcons
}) => {
    const { t } = useTranslation();

    return (
        <TableRow variant="noHover">
            {showIcons && <TableCell width={1} />}

            {fields.map((field) => {
                const { label, afterActions, hidden, tooltip } = field;
                if (!label || afterActions || hidden) {
                    return null;
                }

                return (
                    <TableCell key={`header_${label}`}>
                        <Box display="inline-flex" alignItems="center">
                            <strong>{label}</strong>
                            {tooltip && (
                                <TooltipQ text={t(`Tooltips.${tooltip}`)} />
                            )}
                        </Box>
                    </TableCell>
                );
            })}

            {!noActions && (
                <TableCell align="center" width={130}>
                    <Box display="inline-flex" alignItems="center">
                        <strong>{t('Common.actions')}</strong>
                        {actionsTooltip && (
                            <TooltipQ text={t(`Tooltips.${actionsTooltip}`)} />
                        )}
                    </Box>
                </TableCell>
            )}

            {endFields.map((field) => {
                const { label } = field;
                return (
                    <TableCell
                        key={`header_${label}`}
                        align="center"
                        width={170}
                    >
                        <strong>{label}</strong>
                    </TableCell>
                );
            })}
        </TableRow>
    );
};

const RenderAddRow = ({ colSpan, handleAdd, rows, zeroText }) => {
    if (rows.length) {
        return null;
    }

    return (
        <TableRow variant="noHover">
            <TableCell colSpan={colSpan} style={{ paddingLeft: 0 }}>
                <Button
                    color="primary"
                    variant="outlined"
                    onClick={() => handleAdd()}
                >
                    + {zeroText}
                </Button>
            </TableCell>
        </TableRow>
    );
};

const RenderCheckActions = ({
    handleAdd,
    handleCheck,
    index,
    lastRow,
    row
}) => {
    const { t } = useTranslation();
    const { actionDisabled } = row;

    return (
        <TableCell align="center" key={`row_${index}_check_actions`}>
            <DarkTooltip arrow title={t('Tooltips.Notes.mark_as_complete')}>
                <Button
                    color="primary"
                    variant="outlined"
                    style={{
                        marginRight: '6px',
                        minWidth: 0,
                        padding: '6px'
                    }}
                    disabled={actionDisabled}
                    onClick={() => handleCheck({ index, row })}
                >
                    <AssignmentTurnedIn />
                </Button>
            </DarkTooltip>
            {lastRow && (
                <Button
                    color="primary"
                    variant="contained"
                    style={{
                        marginRight: '-35px',
                        minWidth: 0,
                        padding: '6px'
                    }}
                    onClick={() => handleAdd()}
                >
                    <Add />
                </Button>
            )}
        </TableCell>
    );
};

const RenderActions = ({
    fields,
    handleAdd,
    handleCheck,
    handleRemove,
    index,
    min,
    noActions,
    row,
    rows
}) => {
    if (noActions) {
        return null;
    }

    const { checkAction } = row;

    const totalRows = rows.length;
    const lastRow = index + 1 === totalRows;
    if (checkAction) {
        return (
            <RenderCheckActions
                handleAdd={handleAdd}
                handleCheck={handleCheck}
                index={index}
                lastRow={lastRow}
                row={row}
            />
        );
    }

    const emptyFields = Object.keys(row).filter((i) => {
        const field = row[i];
        if (field === '') {
            return true;
        }
        return false;
    });

    const disabled = !lastRow || emptyFields.length === fields.length;
    return (
        <TableCell align="center" key={`row_${index}_actions`}>
            <Button
                color="primary"
                variant="outlined"
                style={{ marginRight: '6px', minWidth: 0, padding: '6px' }}
                disabled={!(totalRows > min)}
                onClick={() => handleRemove({ index, row })}
            >
                <Remove />
            </Button>
            <Button
                color="primary"
                variant="contained"
                style={{ minWidth: 0, padding: '6px' }}
                disabled={disabled}
                onClick={() => handleAdd()}
            >
                <Add />
            </Button>
        </TableCell>
    );
};

const RenderSelect = ({ disabled, field, handleModify, index, opts, row }) => {
    const { items } = opts;

    let value = row[field];
    if (!items.includes(value)) {
        value = opts.default;
    }

    return (
        <Select
            IconComponent={ExpandMore}
            variant="standard"
            value={value}
            onChange={(e) =>
                handleModify({
                    field,
                    index,
                    value: e.target.value
                })
            }
            style={{ width: '100%' }}
            disabled={disabled}
        >
            {items.map((item) => (
                <MenuItem key={`row_${index}_item_${item}`} value={item}>
                    {item}
                </MenuItem>
            ))}
        </Select>
    );
};

const RenderCheckbox = ({
    disabled,
    field,
    handleModify,
    index,
    opts,
    value
}) => (
    <Checkbox
        checked={!!value}
        disabled={disabled || opts.disabled}
        onChange={(e) =>
            handleModify({ field, index, value: e.target.checked })
        }
    />
);

const RenderTextField = ({
    disabled,
    field,
    handleModify,
    index,
    opts,
    placeholder,
    value
}) => {
    const { multiline } = opts;

    return (
        <TextField
            fullWidth
            defaultValue={value}
            multiline={multiline}
            disabled={disabled || opts.disabled}
            inputProps={disabled ? { sx: { color: 'text.disabled' } } : {}}
            placeholder={placeholder || opts.placeholder}
            onChange={(e) => {
                handleModify({ field, index, value: e.target.value });
            }}
        />
    );
};

const RenderInput = ({ handleModify, index, opts, placeholder, row }) => {
    const { field, type } = opts;

    let value = row[field];
    if (opts.default) {
        value = opts.default;
    }

    if (value === undefined) {
        return null;
    }

    const disabled = row.disabled || opts.disabled;
    switch (type) {
        case 'select':
            return (
                <RenderSelect
                    disabled={disabled}
                    field={field}
                    handleModify={handleModify}
                    index={index}
                    opts={opts}
                    row={row}
                />
            );
        case 'checkbox':
            return (
                <RenderCheckbox
                    disabled={disabled}
                    field={field}
                    handleModify={handleModify}
                    index={index}
                    opts={opts}
                    value={value}
                />
            );
        default:
            return (
                <RenderTextField
                    disabled={disabled}
                    field={field}
                    handleModify={handleModify}
                    index={index}
                    opts={opts}
                    placeholder={placeholder}
                    value={value}
                />
            );
    }
};

const RenderRow = ({
    endFields,
    fields,
    handleAdd,
    handleCheck,
    handleModify,
    handleRemove,
    index,
    min,
    noActions,
    placeholder,
    row,
    rows,
    showIcons
}) => {
    const { icon } = row;

    const key = `row_${index}`;
    return (
        <TableRow variant="noHover">
            {showIcons && (
                <TableCell align="center" style={{ padding: 0 }}>
                    {icon}
                </TableCell>
            )}

            {fields.map((opts) => {
                const { field, hidden, afterActions } = opts;
                if (hidden || afterActions) {
                    return null;
                }

                return (
                    <TableCell key={`${key}_cell_${field}`}>
                        <RenderInput
                            handleModify={handleModify}
                            index={index}
                            opts={opts}
                            placeholder={placeholder}
                            row={row}
                        />
                    </TableCell>
                );
            })}

            <RenderActions
                fields={fields}
                handleAdd={handleAdd}
                handleCheck={handleCheck}
                handleRemove={handleRemove}
                index={index}
                min={min}
                noActions={noActions}
                row={row}
                rows={rows}
            />

            {endFields.map((opts) => (
                <TableCell key={`${key}_cell_${opts.field}`} align="center">
                    <RenderInput
                        handleModify={handleModify}
                        index={index}
                        opts={opts}
                        placeholder={placeholder}
                        row={row}
                    />
                </TableCell>
            ))}
        </TableRow>
    );
};

class MultiInput extends Component {
    constructor(props) {
        super(props);
        this.state = state();
    }

    componentDidMount() {
        const { open } = this.props;
        if (open || open === undefined) {
            this.load();
        }
    }

    componentDidUpdate(prev) {
        const { open, importingRow, reload } = this.props;
        const { loaded } = this.state;

        if (open === undefined) {
            return;
        }

        if (!prev.open && open && !loaded) {
            this.load();
        }

        if (prev.open && !open) {
            this.unload();
        }

        if (!prev.importingRow && !isEmpty(importingRow)) {
            this.handleImport({ row: importingRow });
        }

        if (reload) {
            this.reload();
        }
    }

    componentWillUnmount() {
        this.unload();
    }

    load = () => {
        const { values } = this.props;
        if (!Array.isArray(values)) {
            return;
        }

        // Inputs can have fields that aren't headers
        // These are "defaultFields"
        let { fields } = this.props;
        fields = [...fields, ...defaultFields];

        const fieldNames = [];
        const endFields = [];
        fields.forEach((opts) => {
            const { field, afterActions } = opts;
            fieldNames.push(field);

            // "afterActions" means its an input that will go
            // after the "Actions" column
            if (afterActions) {
                endFields.push(opts);
            }
        });

        const rows = values.map((row) => {
            const map = {};
            fieldNames.forEach((field) => {
                map[field] = row[field];
            });

            if (!row.id) {
                map.id = uuidv4();
            }
            return map;
        });

        this.setState({
            colSpan: fields.length + 1,
            endFields,
            loaded: true,
            rows
        });
    };

    unload = () => {
        const { onReload } = this.props;
        this.setState(state());
        if (onReload) {
            onReload();
        }
    };

    reload = () => {
        this.setState(state(), () => this.load());
    };

    handleAdd = () => {
        const { fields } = this.props;
        const { rows, added } = this.state;
        const id = uuidv4();

        const row = {};
        fields.forEach((opts) => {
            const value = opts.default || '';
            row[opts.field] = value;
            if (!row.id) {
                row.id = id;
            }
        });

        added[id] = row;
        this.setState({ added }, () => {
            rows.push(row);
            this.setRows({ row, rows });
        });
    };

    handleImport = (params) => {
        const { rows } = this.state;
        const { row } = params;

        rows.unshift(row);
        this.setState({ rows });
    };

    handleModify = ({ index, field, value }) => {
        const { values } = this.props;
        const { rows, added, modified } = this.state;

        let data = {};
        if (isset(values, index)) {
            data = values[index];
        }

        const row = { ...data, ...rows[index], [field]: value };
        const { id } = row;
        if (added[id] || row.new) {
            // If it was an added row, modify the 'added' entry
            added[id] = row;
        } else {
            // If it was an original row, modify it
            modified[id] = row;
        }

        this.setState({ added, modified }, () => {
            const rowMap = { ...rows, [index]: row };
            this.setRows({ row, rows: Object.values(rowMap) });
        });
    };

    handleRemove = ({ index, row }) => {
        const { rows, added, removed } = this.state;
        const { id, community } = row;

        if (!community) {
            if (added[id]) {
                // If it was an added row, removed the 'added' entry
                delete added[id];
            } else {
                // If it was an original row, add it as a 'removed' row
                removed[id] = row;
            }
        }

        this.setState({ added, removed }, () => {
            rows.splice(index, 1);
            this.setRows({ remove: true, row, rows });
        });
    };

    handleCheck = (params) => {
        const { onCheck } = this.props;
        const { rows } = this.state;
        const { index } = params;

        rows.splice(index, 1);
        this.setState({ rows });

        if (onCheck) {
            onCheck(params);
        }
    };

    setRows = (params) => {
        const { onChange } = this.props;
        const { added, modified, removed } = this.state;
        const { rows } = params;

        this.setState({ rows });
        onChange({ ...params, added, modified, removed });
    };

    render() {
        const {
            actionsTooltip,
            fields,
            min,
            noActions,
            placeholder,
            showIcons,
            sx,
            zeroText
        } = this.props;
        const { colSpan, endFields, loaded, rows } = this.state;

        if (!loaded) {
            return null;
        }

        return (
            <TableContainer style={sx}>
                <Table size="small">
                    <TableHead>
                        <RenderHeader
                            actionsTooltip={actionsTooltip}
                            endFields={endFields}
                            fields={fields}
                            noActions={noActions}
                            showIcons={showIcons}
                        />
                    </TableHead>
                    <TableBody>
                        <RenderAddRow
                            colSpan={colSpan}
                            handleAdd={this.handleAdd}
                            rows={rows}
                            zeroText={zeroText}
                        />
                        {rows.map((row, index) => (
                            <RenderRow
                                endFields={endFields}
                                fields={fields}
                                handleAdd={this.handleAdd}
                                handleCheck={this.handleCheck}
                                handleModify={this.handleModify}
                                handleRemove={this.handleRemove}
                                index={index}
                                key={`renderRow_${row.id}`}
                                min={min}
                                noActions={noActions}
                                placeholder={placeholder}
                                row={row}
                                rows={rows}
                                showIcons={showIcons}
                            />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    }
}

export default withTranslation()(MultiInput);
